import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  Button,
  Combobox,
  DialogFooter,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
} from '@accure/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';

import { DatePicker } from '@/components/form/inputs/date-picker';
import { TaskSchema } from '@/project-management/entities/task';
import { TaskPriority } from '@/project-management/entities/task-priority';
import { TaskStatus } from '@/project-management/entities/task-status';
import { CreateTaskMutation } from '@/project-management/mutations/create-task.mutation';

export type CreateTaskFormProps = {
  siteId: string;
  workstreamId: string;
  onClose: () => void;
};

const FormSchema = TaskSchema.pick({
  title: true,
  description: true,
  status: true,
  priority: true,
  dueDate: true,
});
type Form = z.infer<typeof FormSchema>;

export function CreateTaskForm({
  siteId,
  workstreamId,
  onClose,
}: CreateTaskFormProps) {
  const createTask = CreateTaskMutation.useMutation();
  const form = useForm<Form>({
    resolver: zodResolver(FormSchema),
  });

  async function handleSubmit(data: Form) {
    await toast.promise(
      createTask.mutateAsync({ siteId, workstreamId, create: data }),
      {
        pending: 'Creating task...',
        success: 'Task created successfully.',
        error: 'Failed to create task. Please try again.',
      },
    );
    onClose();
  }

  return (
    <>
      <Form {...form}>
        <form
          id="add-workstream-create-form"
          onSubmit={form.handleSubmit(handleSubmit)}
          className="space-y-4"
        >
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input placeholder="Task title" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Task Status</FormLabel>
                <FormControl>
                  <Combobox
                    className="w-full"
                    options={Object.values(TaskStatus).map((status) => ({
                      value: status,
                      label: status,
                    }))}
                    onSelect={field.onChange}
                    labels={['a status', 'statuses']}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="priority"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Task Priority</FormLabel>
                <FormControl>
                  <Combobox
                    className="w-full"
                    options={Object.values(TaskPriority).map((priority) => ({
                      value: priority,
                      label: priority,
                    }))}
                    onSelect={field.onChange}
                    labels={['a priority', 'priorities']}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="dueDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Due Date</FormLabel>
                <FormControl>
                  <DatePicker value={field.value} onSelect={field.onChange} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Task description"
                    {...field}
                    rows={5}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="add-workstream-create-form"
          disabled={createTask.isLoading}
        >
          {createTask.isLoading ? (
            <>
              <Loader2 className="size-3.5 animate-spin" />
              Creating...
            </>
          ) : (
            <>Create Task</>
          )}
        </Button>
      </DialogFooter>
    </>
  );
}
